import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { signInWithEmailAndPassword, signOut, sendEmailVerification, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import "./Auth.css";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showUnverified, setShowUnverified] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();

  // Social sign-in handlers
  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      if (result.user && result.user.emailVerified) {
        navigate('/profile');
      }
    } catch (err) {
      console.error('Google sign-in failed', err);
      setError(err.message || 'Google sign-in failed');
    }
  };

  // Apple sign-in removed temporarily

  // Helper for localStorage-based resend throttling
  const getResendMeta = (email) => {
    try {
      const raw = localStorage.getItem('resend_verification');
      if (!raw) return {};
      const map = JSON.parse(raw || '{}');
      return map[email] || {};
    } catch (e) { return {}; }
  }

  const setResendMeta = (email, meta) => {
    try {
      const raw = localStorage.getItem('resend_verification');
      const map = raw ? JSON.parse(raw) : {};
      map[email] = meta;
      localStorage.setItem('resend_verification', JSON.stringify(map));
    } catch (e) { /* ignore */ }
  }

  useEffect(() => {
    let timer = null;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown(c => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    // If user is already logged in AND verified, redirect to profile
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (user.emailVerified) {
          navigate("/profile");
        } else {
           // If logged in but not verified, ensure they are signed out so they stay on login page
           await signOut(auth); 
        }
      }
    });
    return unsubscribe;
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (!user.emailVerified) {
        // Do not immediately sign out; show resend option. But sign out to avoid leaving session.
        await signOut(auth);
        setShowUnverified(true);
        setError("Your account is not activated. Please check your email (including spam folder) and click the activation link.");
        // initialize cooldown from localStorage
        const meta = getResendMeta(email);
        const last = meta.lastSent || 0;
        const now = Date.now();
        const diff = Math.max(0, Math.floor((60_000 - (now - last)) / 1000));
        setCooldown(diff);
        return;
      }
      
      navigate("/profile");
    } catch (err) {
       // Handle specific error codes for better UX
       if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
          setError("Invalid email or password.");
      } else if (err.code === 'auth/too-many-requests') {
          setError("Too many failed login attempts. Please try again later.");
      } else {
          setError(err.message);
      }
    }
  };

  // Resend verification email handler
  const handleResend = async () => {
    setResendMessage("");
    if (!email || !password) {
      setResendMessage('Please enter your email and password to resend verification.');
      return;
    }

    // Throttle via localStorage: max 5 per day, 60s cooldown
    const meta = getResendMeta(email);
    const now = Date.now();
    const last = meta.lastSent || 0;
    const countDay = meta.countDay || 0;
    const day = meta.day || 0;
    const today = new Date().toISOString().slice(0,10);

    let count = countDay;
    if (day !== today) {
      count = 0;
    }
    if (now - last < 60_000) {
      setResendMessage('Please wait before resending.');
      setCooldown(Math.ceil((60_000 - (now - last)) / 1000));
      return;
    }
    if (count >= 5) {
      setResendMessage('You have reached the daily limit for resending verification. Please try again tomorrow or contact support.');
      return;
    }

    try {
      // Reauthenticate by signing in to get a user object to call sendEmailVerification
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await sendEmailVerification(user);
      await signOut(auth);

      // Update localStorage meta
      setResendMeta(email, { lastSent: now, countDay: count + 1, day: today });
      setResendMessage('Verification email resent. Check your inbox (and spam).');
      setCooldown(60);
    } catch (err) {
      console.error('Resend failed', err);
      if (err.code === 'auth/wrong-password') setResendMessage('Wrong password. Please enter correct password to resend.');
      else setResendMessage(err.message || 'Failed to resend verification email.');
    }
  }

  return (
    <div className="auth-container">
      <h2>Login</h2>
      {error && <div className="auth-error">{error}</div>}
      {showUnverified && (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fffaf0' }}>
          <div style={{ marginBottom: 8 }}>Need help activating your account?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={handleResend} disabled={cooldown > 0}>{cooldown > 0 ? `Resend (${cooldown})` : 'Resend verification email'}</button>
            <button className="btn" onClick={() => { navigate('/forgot-password'); }}>Forgot Password</button>
          </div>
          {resendMessage && <div style={{ marginTop: 8, color: '#333' }}>{resendMessage}</div>}
        </div>
      )}
      <form onSubmit={handleLogin} style={{ width: "100%" }}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
      </form>
      <div style={{ marginTop: 12 }}>
        <div style={{ textAlign: 'center', margin: '8px 0', color: '#666' }}>or</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="social-btn google" onClick={handleGoogleSignIn}>
            <span className="social-icon" aria-hidden>
              <svg width="28" height="28" viewBox="0 0 533.5 544.3" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path fill="#4285F4" d="M533.5 278.4c0-18.5-1.5-37.6-4.7-55.5H272v105.1h147.2c-6.3 34-25.1 62.9-53.6 82v67h86.5c50.6-46.6 82.4-115.3 82.4-198.6z"/>
                <path fill="#34A853" d="M272 544.3c72.6 0 133.6-24 178.1-65.3l-86.5-67c-24 16.1-54.6 25.6-91.6 25.6-70.4 0-130.1-47.5-151.5-111.3H31.2v69.8C75.1 486.1 167.2 544.3 272 544.3z"/>
                <path fill="#FBBC05" d="M120.5 328.3c-10.8-32.4-10.8-66.9 0-99.3V159.2H31.2c-39.6 77.4-39.6 169.5 0 246.9l89.3-77.8z"/>
                <path fill="#EA4335" d="M272 107.6c39.3 0 74.7 13.6 102.6 40.4l76.9-76.9C402.9 24 341.9 0 272 0 167.2 0 75.1 58.2 31.2 159.2l89.3 69.1C141.9 155.1 201.6 107.6 272 107.6z"/>
              </svg>
            </span>
            <span>Sign in with Google</span>
          </button>
        </div>
      </div>
      <div style={{ marginTop: "1rem", fontSize: "0.95rem" }}>
        <Link to="/forgot-password">Forgot Password?</Link>
      </div>
      <div style={{ marginTop: "1rem", fontSize: "0.95rem" }}>
        Don't have an account? <Link to="/signup">Sign Up</Link>
      </div>
    </div>
  );
}